import { Plus, Trash2 } from 'lucide-react'
import type { SchemaField, TableColumn } from '../../../../types/schede'
import FieldWrapper from './FieldWrapper'

interface TableFieldProps {
  field: SchemaField
  value: any
  onChange: (value: any) => void
  readOnly?: boolean
  error?: string
}

export default function TableField({ field, value, onChange, readOnly, error }: TableFieldProps) {
  const columns = field.table_columns ?? []
  const rows: Record<string, any>[] = Array.isArray(value) ? value : []
  const minRows = field.table_min_rows ?? 0
  const maxRows = field.table_max_rows ?? 999

  const addRow = () => {
    if (rows.length >= maxRows) return
    const emptyRow: Record<string, any> = {}
    columns.forEach((col) => {
      emptyRow[col.id] = col.type === 'checkbox' ? false : ''
    })
    onChange([...rows, emptyRow])
  }

  const removeRow = (index: number) => {
    if (rows.length <= minRows) return
    const updated = rows.filter((_, i) => i !== index)
    onChange(updated)
  }

  const updateCell = (rowIndex: number, colId: string, cellValue: any) => {
    const updated = rows.map((row, i) => {
      if (i !== rowIndex) return row
      return { ...row, [colId]: cellValue }
    })
    onChange(updated)
  }

  const inputStyle = {
    backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
    borderColor: 'var(--color-border)',
    color: 'var(--color-text-primary)',
  }

  const renderCell = (col: TableColumn, row: Record<string, any>, rowIndex: number) => {
    const cellValue = row[col.id]

    switch (col.type) {
      case 'number':
        return (
          <input
            type="number"
            value={cellValue ?? ''}
            onChange={(e) => updateCell(rowIndex, col.id, e.target.value === '' ? null : Number(e.target.value))}
            readOnly={readOnly}
            className="w-full px-1.5 py-1 text-xs border rounded outline-none"
            style={inputStyle}
          />
        )
      case 'date':
        return (
          <input
            type="date"
            value={cellValue ?? ''}
            onChange={(e) => updateCell(rowIndex, col.id, e.target.value)}
            readOnly={readOnly}
            className="w-full px-1.5 py-1 text-xs border rounded outline-none"
            style={inputStyle}
          />
        )
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={!!cellValue}
            onChange={(e) => updateCell(rowIndex, col.id, e.target.checked)}
            disabled={readOnly}
            className="w-4 h-4"
            style={{ accentColor: 'var(--color-primary, #3b82f6)' }}
          />
        )
      case 'select':
        return (
          <select
            value={cellValue ?? ''}
            onChange={(e) => updateCell(rowIndex, col.id, e.target.value)}
            disabled={readOnly}
            className="w-full px-1.5 py-1 text-xs border rounded outline-none"
            style={inputStyle}
          >
            <option value="">--</option>
            {col.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )
      default: // text
        return (
          <input
            type="text"
            value={cellValue ?? ''}
            onChange={(e) => updateCell(rowIndex, col.id, e.target.value)}
            readOnly={readOnly}
            className="w-full px-1.5 py-1 text-xs border rounded outline-none"
            style={inputStyle}
          />
        )
    }
  }

  return (
    <FieldWrapper field={field} error={error}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className="px-1.5 py-1 text-left font-medium"
                  style={{
                    borderBottom: '1px solid var(--color-border)',
                    color: 'var(--color-text-secondary)',
                    width: col.width,
                  }}
                >
                  {col.label}
                  {col.required && <span style={{ color: 'var(--color-danger, #ef4444)' }}> *</span>}
                </th>
              ))}
              {!readOnly && (
                <th
                  className="px-1.5 py-1 w-8"
                  style={{ borderBottom: '1px solid var(--color-border)' }}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col) => (
                  <td
                    key={col.id}
                    className="px-1 py-0.5"
                    style={{ borderBottom: '1px solid var(--color-border)' }}
                  >
                    {renderCell(col, row, rowIndex)}
                  </td>
                ))}
                {!readOnly && (
                  <td
                    className="px-1 py-0.5 text-center"
                    style={{ borderBottom: '1px solid var(--color-border)' }}
                  >
                    <button
                      type="button"
                      onClick={() => removeRow(rowIndex)}
                      disabled={rows.length <= minRows}
                      className="p-0.5 rounded hover:opacity-80 disabled:opacity-30"
                      style={{ color: 'var(--color-danger, #ef4444)' }}
                      title="Rimuovi"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + (readOnly ? 0 : 1)}
                  className="px-2 py-3 text-center"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  Nessuna riga
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={addRow}
          disabled={rows.length >= maxRows}
          className="mt-1.5 flex items-center gap-1 px-2 py-1 text-xs border rounded hover:opacity-80 disabled:opacity-40"
          style={{
            borderColor: 'var(--color-border)',
            color: 'var(--color-primary, #3b82f6)',
            backgroundColor: 'var(--color-bg-card)',
          }}
        >
          <Plus className="w-3 h-3" />
          Aggiungi riga
        </button>
      )}
    </FieldWrapper>
  )
}
